import { useCallback } from 'react';
import { Feature, hasFeature } from '@fytness/shared';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { usePaywall } from '~/purchases/hooks/usePaywall';

/**
 * Returns a guard that runs `action` when the current user's plan unlocks
 * `feature` (via the shared, subsumption-aware `hasFeature`), and otherwise opens
 * the promotional paywall modal. This is the single client-side gate for Pro
 * creation entry points — the same `Feature` map is enforced on the backend, so
 * this is UX, not the security boundary.
 *
 * @example
 * const guard = useFeatureGuard();
 * const handleAdd = () =>
 *   guard(Feature.CUSTOM_FOODS, () => showModal(MealFormDialog, { ... }));
 */
export const useFeatureGuard = () => {
  const { activeEntitlements } = usePurchases();
  const { openPaywall } = usePaywall();
  return useCallback(
    (feature: Feature, action: () => void) => {
      if (hasFeature(activeEntitlements, feature)) {
        action();
      } else {
        openPaywall();
      }
    },
    [activeEntitlements, openPaywall],
  );
};
